import { useDeferredValue, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Slider } from "@/components/ui/slider";
import { Eyebrow } from "@/components/brand/Eyebrow";
import {
  averageYears2to5,
  formatUSD,
  overridesYearOne,
  personalYearOne,
  projectedYear,
  projectionSeries,
  type CompParams,
} from "@/lib/compCalc";

type SliderRowProps = {
  label: string;
  helper: string;
  display: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
};

function SliderRow({ label, helper, display, value, min, max, step, onChange }: SliderRowProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-4">
        <label className="font-sans text-[13px] font-semibold text-charcoal-900">{label}</label>
        <span className="font-serif text-[32px] leading-none text-bronze-600">{display}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        className="py-1"
      />
      <p className="font-sans text-[12px] text-charcoal-500">{helper}</p>
    </div>
  );
}

export function CompensationCalculator() {
  const [params, setParams] = useState<CompParams>({
    policiesPerMonth: 5,
    avgPremium: 1800,
    firstYearPct: 85,
    renewalPct: 5,
    recruitsPerYear: 2,
  });

  const deferred = useDeferredValue(params);

  const data = useMemo(() => {
    const personalY1 = Math.round(personalYearOne(deferred));
    const overridesY1 = Math.round(overridesYearOne(deferred));
    return {
      y1: personalY1 + overridesY1,
      personalY1,
      overridesY1,
      avg2to5: averageYears2to5(deferred),
      y10: projectedYear(deferred, 10),
      series: projectionSeries(deferred),
    };
  }, [deferred]);

  const update = <K extends keyof CompParams>(key: K) => (v: number) =>
    setParams((p) => ({ ...p, [key]: v }));

  return (
    <section className="bg-white py-24">
      <div className="container mx-auto px-6 md:px-10">
        <div className="max-w-[720px] mx-auto text-center mb-12">
          <Eyebrow>Compensation Snapshot</Eyebrow>
          <h2 className="font-serif text-4xl md:text-5xl text-navy-900 mt-4 leading-[1.15]">
            We don't sell the dream.{" "}
            <em className="italic text-bronze-600">We show the math.</em>
          </h2>
          <p className="max-w-[640px] mx-auto mt-5 text-charcoal-500 font-sans text-[16px] leading-[1.65]">
            Below are realistic projections based on Currency Thoughts strategist production. Adjust
            the sliders to see what your year could look like.
          </p>
        </div>

        <div
          className="max-w-[960px] mx-auto bg-white p-8 md:p-10"
          style={{
            borderRadius: "4px",
            boxShadow: "0 10px 40px -10px rgba(168,139,92,0.18), 0 2px 8px rgba(168,139,92,0.08)",
            border: "1px solid rgba(168,139,92,0.18)",
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-10">
            {/* LEFT — Inputs */}
            <div className="space-y-6">
              <SliderRow
                label="Policies each month"
                helper="Top producers: 8-12. New strategists typically: 3-6 in year one."
                display={String(params.policiesPerMonth)}
                value={params.policiesPerMonth}
                min={1}
                max={15}
                step={1}
                onChange={update("policiesPerMonth")}
              />
              <SliderRow
                label="Average annual policy value"
                helper="Permanent coverage: higher end. Temporary coverage: lower end."
                display={formatUSD(params.avgPremium)}
                value={params.avgPremium}
                min={500}
                max={5000}
                step={100}
                onChange={update("avgPremium")}
              />
              <SliderRow
                label="First-year commission %"
                helper="Carrier-dependent. 85-95% is typical at our contract levels."
                display={`${params.firstYearPct}%`}
                value={params.firstYearPct}
                min={50}
                max={110}
                step={5}
                onChange={update("firstYearPct")}
              />
              <SliderRow
                label="Renewals % per year (Y2-10 average)"
                helper="This is where the math gets interesting — renewals compound."
                display={`${params.renewalPct}%`}
                value={params.renewalPct}
                min={0}
                max={15}
                step={1}
                onChange={update("renewalPct")}
              />
              <SliderRow
                label="Recruits per year (overrides)"
                helper="Override income on team production. Set to 0 if you only want personal."
                display={String(params.recruitsPerYear)}
                value={params.recruitsPerYear}
                min={0}
                max={12}
                step={1}
                onChange={update("recruitsPerYear")}
              />
            </div>

            {/* RIGHT — Output */}
            <div className="bg-cream-50 p-6 md:p-7" style={{ borderRadius: "4px" }}>
              <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-charcoal-500">
                Year 1 Estimate
              </p>
              <p className="font-serif text-[56px] leading-[1.05] text-bronze-600 mt-2 tabular-nums">
                {formatUSD(data.y1)}
              </p>
              <p className="font-sans text-[11px] text-charcoal-500 mt-1">
                Personal production + team overrides
              </p>

              <dl className="mt-6 divide-y" style={{ borderColor: "rgba(168,139,92,0.4)" }}>
                {[
                  { k: "Personal Y1 commissions", v: data.personalY1 },
                  { k: "Recruiting overrides Y1", v: data.overridesY1 },
                  { k: "Annual avg, Years 2-5", v: data.avg2to5 },
                  { k: "Year 10 trajectory", v: data.y10 },
                ].map((row) => (
                  <div
                    key={row.k}
                    className="flex items-baseline justify-between py-2.5"
                    style={{ borderTop: "1px solid rgba(168,139,92,0.4)" }}
                  >
                    <dt className="font-sans text-[13px] text-charcoal-900">{row.k}</dt>
                    <dd className="font-sans text-[13px] font-semibold text-navy-900 tabular-nums">
                      {formatUSD(row.v)}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 h-[180px] -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(168,139,92,0.18)" vertical={false} />
                    <XAxis
                      dataKey="year"
                      tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "Inter" }}
                      axisLine={{ stroke: "rgba(168,139,92,0.4)" }}
                      tickLine={false}
                      tickFormatter={(y) => `Y${y}`}
                    />
                    <YAxis
                      tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "Inter" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) =>
                        v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`
                      }
                      width={48}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(168,139,92,0.08)" }}
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid rgba(168,139,92,0.4)",
                        borderRadius: 4,
                        fontFamily: "Inter",
                        fontSize: 12,
                      }}
                      formatter={(v: number) => [formatUSD(v), "Income"]}
                      labelFormatter={(y) => `Year ${y}`}
                    />
                    <Bar dataKey="income" fill="#A88B5C" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div
            className="mt-8 p-4 italic font-sans text-[12px] text-charcoal-500 leading-[1.6]"
            style={{ border: "1px solid rgba(168,139,92,0.4)", borderRadius: "4px" }}
          >
            Projections are hypothetical, for educational purposes only. Actual results depend on
            effort, market conditions, license status, carriers, products sold, persistency, and
            many other factors. Currency Thoughts does not guarantee any income level. Review the
            formal strategist agreement for actual compensation terms.
          </div>
        </div>

        <div className="text-center mt-10">
          <p className="font-sans text-[15px] text-charcoal-900 mb-4">
            Want to walk through this with someone?
          </p>
          <Link
            to="/book"
            className="inline-flex items-center justify-center h-11 px-6 text-[13px] font-sans font-semibold uppercase tracking-[0.05em] border border-bronze-600 text-navy-900 hover:bg-bronze-400 hover:border-bronze-400 transition-colors"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Book a 15-min income walkthrough
          </Link>
        </div>
      </div>
    </section>
  );
}

export default CompensationCalculator;